// NOTE (Ben, 2026-03-04): Adapted from https://elements.ai-sdk.dev/components/conversation

import type { ComponentProps } from "react";

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import { useIsPanelResizing } from "@agent-ui/store/editorStore";
import { Button } from "@replo/design-system/components/button/Button";
import twMerge from "@replo/design-system/utils/twMerge";
import { ArrowDownIcon } from "lucide-react";
import { StickToBottom, useStickToBottomContext } from "use-stick-to-bottom";

type ConversationProps = ComponentProps<typeof StickToBottom>;

export const Conversation = ({ className, ...props }: ConversationProps) => (
  <StickToBottom
    className={twMerge("relative flex-1 overflow-y-hidden", className)}
    initial="smooth"
    resize="smooth"
    damping={0.25}
    stiffness={0.2}
    mass={0.75}
    role="log"
    {...props}
  />
);

/**
 * Disables the stick-to-bottom scroll chasing while the user is actively
 * dragging the panel resize handle. Without this, the library fires
 * scrollToBottom on every animation frame during resize, creating visible
 * scroll jumps. When the drag ends, we re-engage stick-to-bottom so the
 * user lands back at the bottom if they were there before.
 */
export const PanelResizeScrollHandler = () => {
  const isPanelResizing = useIsPanelResizing();
  const { stopScroll, scrollToBottom, isAtBottom } = useStickToBottomContext();
  const wasAtBottomBeforeDragRef = useRef(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: isAtBottom is intentionally read only at drag start, not as a trigger
  useEffect(() => {
    if (isPanelResizing) {
      wasAtBottomBeforeDragRef.current = isAtBottom;
      stopScroll();
    } else if (wasAtBottomBeforeDragRef.current) {
      void scrollToBottom({ animation: "instant" });
    }
  }, [isPanelResizing, stopScroll, scrollToBottom]);

  return null;
};

type ConversationContentProps = ComponentProps<typeof StickToBottom.Content>;

export const ConversationContent = ({
  className,
  ...props
}: ConversationContentProps) => (
  <StickToBottom.Content
    className={twMerge("flex flex-col gap-3 py-3", className)}
    {...props}
  />
);

type ConversationEmptyStateProps = ComponentProps<"div"> & {
  title?: string;
  description?: string;
  icon?: React.ReactNode;
};

export const ConversationEmptyState = ({
  className,
  title = "No messages yet",
  description = "Start a conversation to see messages here",
  icon,
  children,
  ...props
}: ConversationEmptyStateProps) => (
  <div
    className={twMerge(
      "flex flex-1 w-full flex-col items-center justify-center gap-3",
      className,
    )}
    {...props}
  >
    {children ?? (
      <>
        {icon && <div className="text-muted-foreground">{icon}</div>}
        <div className="space-y-1">
          <h3 className="font-medium text-sm">{title}</h3>
          {description && (
            <p className="text-muted-foreground text-sm">{description}</p>
          )}
        </div>
      </>
    )}
  </div>
);

type ConversationScrollButtonProps = Omit<
  ComponentProps<typeof Button>,
  "onClick"
>;

export const ConversationScrollButton = ({
  className,
  ...props
}: ConversationScrollButtonProps) => {
  const { isAtBottom, scrollToBottom } = useStickToBottomContext();

  const handleScrollToBottom = useCallback(() => {
    void scrollToBottom();
  }, [scrollToBottom]);

  return (
    !isAtBottom && (
      <Button
        UNSAFE_className={twMerge(
          "absolute bottom-4 left-[50%] translate-x-[-50%] rounded-full",
          className,
        )}
        onClick={handleScrollToBottom}
        icon
        size="sm"
        type="button"
        variant="secondary"
        {...props}
      >
        <ArrowDownIcon />
      </Button>
    )
  );
};

/**
 * Spacer that fills remaining viewport space below the latest user message.
 * This makes stick-to-bottom position the user's message near the top of
 * the viewport (like v0/ChatGPT). As the assistant response grows, the
 * spacer shrinks, reaching 0 once the response fills the viewport.
 */
export const ConversationBottomSpacer = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  useLayoutEffect(() => {
    const spacerEl = ref.current;
    if (!spacerEl) {
      return;
    }

    // spacerEl -> StickToBottom.Content -> StickToBottom scroll div
    const contentEl = spacerEl.parentElement;
    const scrollEl = contentEl?.parentElement;
    if (!contentEl || !scrollEl) {
      return;
    }

    const recalc = () => {
      const viewportHeight = scrollEl.clientHeight;

      // Find all user messages and pick the last one
      const userMsgs = contentEl.querySelectorAll(".is-user");
      const lastUserMsg = userMsgs[userMsgs.length - 1] as
        | HTMLElement
        | undefined;

      if (!lastUserMsg) {
        setHeight(0);
        return;
      }

      // Use getBoundingClientRect to get the distance from the top of the
      // last user message to the bottom of real content (excluding spacer).
      // This avoids offsetParent issues with nested containers.
      const contentRect = contentEl.getBoundingClientRect();
      const userMsgRect = lastUserMsg.getBoundingClientRect();
      const userMsgTopInContent = userMsgRect.top - contentRect.top;
      const realContentHeight = contentEl.scrollHeight - spacerEl.offsetHeight;
      const contentFromUserMsg = realContentHeight - userMsgTopInContent;

      // Spacer = viewport height - height from user message to end of content.
      // Shrinks as the response grows; reaches 0 when it fills the viewport.
      // 12px extra so the user message doesn't sit flush at the top
      const remaining = Math.max(0, viewportHeight - contentFromUserMsg - 12);
      setHeight(remaining);
    };

    recalc();

    const observer = new ResizeObserver(recalc);
    observer.observe(contentEl);
    observer.observe(scrollEl);

    return () => observer.disconnect();
  }, []);

  return <div ref={ref} className="shrink-0" style={{ height }} />;
};
